import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  Typography,
  Link as MuiLink,
  CircularProgress,
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../services/axiosInstance';
import {
  isValidDni,
  isValidPhone,
  MAX_DNI_PHONE_LENGTH,
  MAX_SURNAME_LENGTH,
  normalizeDni,
  normalizePhone,
} from '../utils/validation';

export function Register() {
  // Form State
  const [createName, setCreateName] = useState('');
  const [surname, setSurname] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [dob, setDob] = useState('');
  const [dni, setDni] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false); // Estado para el botón

  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();

    // Validar que todos los campos estén llenos
    if (
      !createName ||
      !surname ||
      !email ||
      !password ||
      !dob ||
      !dni ||
      !phone
    ) {
      toast.error('Todos los campos son obligatorios.');
      return;
    }

    if (!isValidDni(dni)) {
      toast.error(
        'El DNI/NIE es obligatorio y debe tener máximo 20 caracteres.'
      );
      return;
    }

    if (!isValidPhone(phone)) {
      toast.error(
        'El teléfono es obligatorio y debe tener máximo 20 caracteres.'
      );
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post(`/users/register`, {
        name: createName.trim(),
        surname: surname.trim(),
        email: email.trim().toLowerCase(),
        password,
        dob,
        dni: normalizeDni(dni),
        phone: normalizePhone(phone),
      });

      if (response.success) {
        toast.success(
          'Registro exitoso. Revisa tu correo para confirmar tu cuenta.'
        );
        setTimeout(() => navigate('/'), 6000);
      } else {
        toast.error(
          response.message === 'Email already exists'
            ? 'El email ya está registrado.'
            : 'Error al registrar.'
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          'Error al crear la cuenta. Inténtalo de nuevo.'
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f4f6f8',
        padding: 2,
      }}
    >
      <Card sx={{ width: 400, boxShadow: 3, textAlign: 'center' }}>
        {/* Logo */}
        <Box
          component="img"
          src="logo.png" // Reemplaza con la ruta de tu logo
          alt="Logo"
          sx={{
            width: 100,
            height: 'auto',
            margin: '0 auto',
            marginTop: 3,
            marginBottom: 2,
          }}
        />

        <CardContent>
          <Typography variant="h5" textAlign="center" gutterBottom>
            Registro
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label="Nombre"
              type="text"
              variant="outlined"
              fullWidth
              value={createName}
              onChange={e => setCreateName(e.target.value)}
              required
            />
            <TextField
              label="Apellidos"
              type="text"
              variant="outlined"
              fullWidth
              value={surname}
              onChange={e => setSurname(e.target.value)}
              inputProps={{ maxLength: MAX_SURNAME_LENGTH }}
              required
            />
            <TextField
              label="Correo Electrónico"
              type="email"
              variant="outlined"
              fullWidth
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <TextField
              label="DNI"
              type="text"
              variant="outlined"
              fullWidth
              value={dni}
              inputProps={{ maxLength: MAX_DNI_PHONE_LENGTH }}
              onChange={e => setDni(e.target.value.toUpperCase())}
              required
            />
            <TextField
              label="Teléfono"
              type="tel"
              variant="outlined"
              fullWidth
              value={phone}
              inputProps={{ maxLength: MAX_DNI_PHONE_LENGTH }}
              onChange={e => setPhone(e.target.value)}
              required
            />
            <TextField
              label="Contraseña"
              type="password"
              variant="outlined"
              fullWidth
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            <TextField
              label="Fecha de Nacimiento"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dob}
              onChange={e => setDob(e.target.value)}
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 1 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Crear una Cuenta'
              )}
            </Button>
          </Box>
        </CardContent>
        <CardActions
          sx={{
            flexDirection: 'column',
            gap: 1,
            textAlign: 'center',
            mt: 2,
            pb: 2,
          }}
        >
          <Typography variant="body2" textAlign="center">
            ¿Ya tienes una cuenta?{' '}
            <MuiLink component={Link} to="/" underline="none" color="primary">
              Inicia sesión aquí
            </MuiLink>
          </Typography>
        </CardActions>
      </Card>
    </Box>
  );
}

export default Register;
